import { useRef } from "react";
import Lottie from "lottie-react";
import Marquee from "react-fast-marquee";
import vibecityAnim from "@/assets/vibecity2.json";
import { ReactComponent as Divider } from "@/assets/hero-dividers.svg";
import ScrambleText from "@/components/ScrambleText";

const CITIES = [
    "Bengaluru",
    "Paris",
    "Sydney",
    "Dubai",
    "New York",
    "London",
    "Miami",
    "San Francisco",
    "Singapore",
    "Berlin",
];

export default function HeroLottie() {
    const lottieRef = useRef(null);

    // The Lottie is authored ~1440×800 (1.8:1 landscape). On a 390px
    // portrait viewport it doesn't fit cleanly, so we render a dedicated
    // mobile composition built from static SVG + DOM elements.
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

    if (isMobile) {
        return <MobileHero />;
    }

    return (
        <section
            data-testid="hero-lottie"
            className="relative w-full overflow-hidden bg-[#0E100F]"
            style={{ height: "100svh", minHeight: 560 }}
        >
            {/* Lottie BACKGROUND only — petals/glows. No baked-in text. */}
            <div className="absolute inset-0 z-0">
                <Lottie
                    lottieRef={lottieRef}
                    animationData={vibecityAnim}
                    loop
                    autoplay
                    rendererSettings={{
                        preserveAspectRatio: "xMidYMid slice",
                    }}
                    style={{
                        width: "100%",
                        height: "100%",
                    }}
                />
            </div>

            {/* Foreground content — vertically centered between header
                (80px) and bottom cities marquee (96px). All elements
                are independent DOM nodes that can be styled / animated
                separately from the background Lottie. */}
            <div
                className="absolute z-10 flex flex-col items-center w-full pointer-events-none"
                style={{
                    top: 80,
                    bottom: 96,
                    left: 0,
                    right: 0,
                    justifyContent: "center",
                    paddingLeft: 24,
                    paddingRight: 24,
                }}
            >
                {/* "emergent" eyebrow logo */}
                <img
                    src="/assets/emergent-logo.svg"
                    alt="emergent"
                    style={{
                        height: 28,
                        width: "auto",
                        marginBottom: 28,
                        opacity: 0.85,
                    }}
                />

                {/* VIBECITY pixel wordmark (animated font-scramble) */}
                <ScrambleText
                    text="VIBECITY"
                    testId="hero-wordmark"
                    style={{
                        color: "#F7F4DA",
                        fontSize: "clamp(120px, 17vw, 260px)",
                        lineHeight: 0.9,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                        display: "inline-block",
                    }}
                />

                {/* Tagline */}
                <div
                    data-testid="hero-tagline"
                    style={{
                        marginTop: 36,
                        maxWidth: 920,
                        fontFamily:
                            '"DIN Alternate", "DIN Alternate Bold", "Figtree", "Inter", system-ui, sans-serif',
                        fontWeight: 700,
                        fontSize: "clamp(22px, 2.2vw, 38px)",
                        lineHeight: 1.2,
                        letterSpacing: "0.005em",
                        color: "#F7F4DA",
                        textAlign: "center",
                    }}
                >
                    {"{ Local ambassadors. Global movement. }"}
                </div>

                {/* CTA */}
                <a
                    href="/apply"
                    data-testid="hero-cta-apply-now"
                    className="inline-flex items-center justify-center hover:brightness-110 transition-[filter] pointer-events-auto"
                    style={{
                        marginTop: 72,
                        padding: "14px 32px",
                        minWidth: 280,
                        height: 56,
                        background:
                            "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                        boxShadow:
                            "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                        borderRadius: 14,
                        fontFamily:
                            '"Figtree", "Inter", system-ui, sans-serif',
                        fontWeight: 700,
                        fontSize: 17,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: "#0E100F",
                        textDecoration: "none",
                    }}
                >
                    Lead a Chapter
                </a>
            </div>

            {/* Scrolling cities marquee, bottom-anchored */}
            <div
                data-testid="hero-city-marquee"
                className="absolute inset-x-0 bottom-0 z-20"
                style={{
                    background: "#131514",
                    border: "1px solid #434343",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    height: "max(64px, calc(100vw * 86 / 1440 + 6px))",
                }}
            >
                <Marquee
                    gradient={false}
                    speed={55}
                    pauseOnHover
                    className="h-full"
                >
                    <div className="flex items-center h-full whitespace-nowrap">
                        {CITIES.concat(CITIES).map((c, i) => (
                            <span
                                key={i}
                                className="flex items-center pr-[33px]"
                                style={{ gap: "33px" }}
                            >
                                <Divider
                                    aria-hidden
                                    style={{
                                        width: 20,
                                        height: 20,
                                        flex: "none",
                                    }}
                                />
                                <span
                                    style={{
                                        fontFamily:
                                            '"Figtree", "Inter", system-ui, sans-serif',
                                        fontWeight: 700,
                                        fontSize: "clamp(20px, 2.2vw, 30px)",
                                        lineHeight: 1.24,
                                        textTransform: "capitalize",
                                        color: "#FFFCE1",
                                    }}
                                >
                                    {c}
                                </span>
                            </span>
                        ))}
                    </div>
                </Marquee>
            </div>
        </section>
    );
}

function MobileHero() {
    return (
        <section
            data-testid="hero-lottie"
            className="relative w-full overflow-hidden bg-[#0E100F]"
            style={{ minHeight: "100svh", height: "100svh" }}
        >
            {/* SVG background with petals + glows */}
            <img
                src="/assets/hero-bg-mobile.svg"
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full pointer-events-none select-none"
                style={{ objectFit: "cover", objectPosition: "center top" }}
            />

            {/* Foreground content — vertically centered group between
                header and bottom cities marquee */}
            <div
                className="relative z-10 flex flex-col items-center w-full"
                style={{
                    position: "absolute",
                    top: 80,
                    bottom: 56, // height of cities marquee
                    left: 0,
                    right: 0,
                    justifyContent: "center",
                    paddingLeft: 16,
                    paddingRight: 16,
                }}
            >
                {/* "emergent" eyebrow logo */}
                <img
                    src="/assets/emergent-logo.svg"
                    alt="emergent"
                    style={{
                        height: 22,
                        width: "auto",
                        marginBottom: 20,
                        opacity: 0.85,
                    }}
                />

                {/* VIBECITY pixel wordmark (animated font-scramble) */}
                <ScrambleText
                    text="VIBECITY"
                    testId="hero-wordmark-mobile"
                    style={{
                        color: "#F7F4DA",
                        fontSize: "clamp(56px, 16vw, 110px)",
                        lineHeight: 0.92,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                        display: "inline-block",
                        whiteSpace: "nowrap",
                    }}
                />

                {/* Tagline — two lines on mobile */}
                <div
                    data-testid="hero-tagline-mobile"
                    style={{
                        marginTop: 24,
                        textAlign: "center",
                        color: "#F7F4DA",
                        fontFamily:
                            '"DIN Alternate", "DIN Alternate Bold", "Figtree", "Inter", system-ui, sans-serif',
                        fontWeight: 700,
                        fontSize: 16,
                        lineHeight: 1.3,
                        letterSpacing: "0.005em",
                    }}
                >
                    <div style={{ whiteSpace: "nowrap" }}>
                        {"{ Local ambassadors."}
                    </div>
                    <div style={{ whiteSpace: "nowrap" }}>
                        {"Global movement. }"}
                    </div>
                </div>

                {/* CTA */}
                <a
                    href="/apply"
                    data-testid="hero-cta-apply-now-mobile"
                    className="inline-flex items-center justify-center hover:brightness-110 transition-[filter]"
                    style={{
                        marginTop: 48,
                        padding: "12px 28px",
                        width: "min(80%, 280px)",
                        height: 48,
                        background:
                            "linear-gradient(180deg, #EDE9C8 11.84%, #807F6F 138.81%)",
                        boxShadow:
                            "inset 0 4px 7.1px #FFFFFF, inset 0 -1px 15.1px #FFFFFF",
                        borderRadius: 12,
                        fontFamily:
                            '"Figtree", "Inter", system-ui, sans-serif',
                        fontWeight: 700,
                        fontSize: 15,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: "#0E100F",
                        textDecoration: "none",
                    }}
                >
                    Lead a Chapter
                </a>
            </div>

            {/* Cities marquee anchored to bottom */}
            <div
                data-testid="hero-city-marquee"
                className="absolute inset-x-0 bottom-0 z-20"
                style={{
                    background: "#131514",
                    border: "1px solid #434343",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    height: 56,
                }}
            >
                <Marquee
                    gradient={false}
                    speed={45}
                    pauseOnHover
                    className="h-full"
                >
                    <div className="flex items-center h-full whitespace-nowrap">
                        {CITIES.concat(CITIES).map((c, i) => (
                            <span
                                key={i}
                                className="flex items-center"
                                style={{ gap: 18, paddingRight: 18 }}
                            >
                                <Divider
                                    aria-hidden
                                    style={{
                                        width: 14,
                                        height: 14,
                                        flex: "none",
                                    }}
                                />
                                <span
                                    style={{
                                        fontFamily:
                                            '"Figtree", "Inter", system-ui, sans-serif',
                                        fontWeight: 700,
                                        fontSize: 16,
                                        lineHeight: 1.24,
                                        textTransform: "capitalize",
                                        color: "#FFFCE1",
                                    }}
                                >
                                    {c}
                                </span>
                            </span>
                        ))}
                    </div>
                </Marquee>
            </div>
        </section>
    );
}

function Petal() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            aria-hidden
            className="shrink-0"
        >
            <g fill="#0a0a0a">
                <ellipse cx="12" cy="4" rx="3.4" ry="6" />
                <ellipse cx="12" cy="20" rx="3.4" ry="6" />
                <ellipse cx="4" cy="12" rx="6" ry="3.4" />
                <ellipse cx="20" cy="12" rx="6" ry="3.4" />
            </g>
        </svg>
    );
}
